import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { flowQuestions } from '../data/questions';

const { FiArrowLeft, FiArrowRight, FiCheck } = FiIcons;

const QuizSection = ({ onComplete, userInfo }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: parseInt(value)
    }));
  };

  const handleNext = () => {
    if (currentQuestion < flowQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const progress = ((currentQuestion + 1) / flowQuestions.length) * 100;
  const currentQ = flowQuestions[currentQuestion];
  const isAnswered = answers[currentQ.id] !== undefined;

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        {/* Progress Bar */}
        <div className="bg-gray-200 h-2">
          <motion.div
            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="p-8 md:p-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-sm text-gray-500">
                Frage {currentQuestion + 1} von {flowQuestions.length}
              </p>
              <p className="text-sm text-blue-600 font-medium">
                Tätigkeit: {userInfo.activity}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Fortschritt</p>
              <p className="text-lg font-semibold text-gray-900">
                {Math.round(progress)}%
              </p>
            </div>
          </div>

          {/* Question */}
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-relaxed">
              {currentQ.text}
            </h2>

            {/* Answer Options */}
            <div className="space-y-3">
              {currentQ.type === 'scale' ? (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  {currentQ.options.map((option, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleAnswer(currentQ.id, index + 1)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                        answers[currentQ.id] === index + 1
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="font-semibold text-lg mb-1">{index + 1}</div>
                      <div className="text-sm">{option}</div>
                    </motion.button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {currentQ.options.map((option, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleAnswer(currentQ.id, index + 1)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                        answers[currentQ.id] === index + 1
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="font-medium">{option}</div>
                    </motion.button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                currentQuestion === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Zurück
            </button>

            <div className="flex space-x-2">
              {flowQuestions.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentQuestion
                      ? 'bg-blue-500'
                      : answers[flowQuestions[index].id]
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              disabled={!isAnswered}
              className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                isAnswered
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 transform hover:scale-105'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {currentQuestion === flowQuestions.length - 1 ? (
                <>
                  Auswertung
                  <SafeIcon icon={FiCheck} className="ml-2" />
                </>
              ) : (
                <>
                  Weiter
                  <SafeIcon icon={FiArrowRight} className="ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default QuizSection;